import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SizeType, VariantType } from '@/types';
import { MultiCheckbox } from './MultiCheckbox';
import { FilterOption, FiltersValues } from './types';
import { generateTestId } from '@/utils/shared/generateTestIds';

interface DropdownFilterProps {
  disabled?: boolean;
  variant?: VariantType;
  size?: SizeType;
  filterLabel: string;
  filterOptions: FilterOption[];
  orientation?: 'vertical' | 'horizontal';
  currentFiltersValues: FiltersValues;
  handleFiltersValuesChange: (filtersValues: FiltersValues) => void;
  isSelected: boolean;
  customItemRender?: (option: FilterOption) => React.ReactNode;
  customCounterRender?: (count: number) => React.ReactNode;
}

export const DropdownFilter = ({
  filterOptions,
  disabled,
  variant,
  size,
  filterLabel,
  orientation = 'vertical',
  currentFiltersValues,
  handleFiltersValuesChange,
  isSelected,
  customItemRender,
  customCounterRender,
}: DropdownFilterProps) => {
  const [isOpened, setIsOpened] = useState(false);

  return (
    <DropdownMenu onOpenChange={setIsOpened} open={isOpened} modal={true}>
      <DropdownMenuTrigger
        asChild
        className={cn('px-4 rounded-lg data-[state=open]:ring-2 data-[state=open]:ring-primary-600 self-center')}
      >
        <Button
          variant={variant || 'outline'}
          size={size}
          disabled={disabled}
          data-testid={generateTestId(`filter_${filterLabel}`)}
          className={cn(isSelected && 'bg-primary-50')}
        >
          {isSelected && <div className="w-2 h-2 rounded-full bg-primary-600 mx-2" />}
          <span className="select-none">{filterLabel}</span>
          <span className={`${isOpened && 'rotate-[180deg]'}`}>
            <ChevronDown />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-xl">
        <div
          className={cn(
            'min-w-64 p-1 max-h-[70vh] xl:max-h-[80vh] overflow-y-auto overflow-x-hidden',
            `${orientation === 'horizontal' && 'flex gap-6 '}`
          )}
        >
          <MultiCheckbox
            filterLabel={filterLabel}
            filterOptions={filterOptions}
            filtersValues={currentFiltersValues}
            handleFiltersValuesChange={handleFiltersValuesChange}
            className="p-2 rounded-xl"
            customItemRender={customItemRender}
            customCounterRender={customCounterRender}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
